import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserPage from 'flarum/forum/components/UserPage';
import PostsUserPage from 'flarum/forum/components/PostsUserPage';
import DiscussionsUserPage from 'flarum/forum/components/DiscussionsUserPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostControls from 'flarum/forum/utils/PostControls';
import UserPointsPage from './components/UserPointsPage';
import UserLeaderboardPage from './components/UserLeaderboardPage';
import UserLikesPage from './components/UserLikesPage';
import UserGroupsPage from './components/UserGroupsPage';
import UserProfilePanel from './components/UserProfilePanel';
import Breadcrumb from './components/Breadcrumb';
import listItems from 'flarum/common/helpers/listItems';

app.initializers.add('tf/custom-profile', () => {
  // 重写默认的 user 路由，让它指向 discussions 页面而不是 posts
  app.routes['user'] = {
    path: '/u/:username',
    component: DiscussionsUserPage
  };

  // 重写 user.posts 路由，让它使用独立的路径 /u/:username/posts
  // 这样 /u/:username 会匹配 user 路由（discussions），/u/:username/posts 会匹配 user.posts 路由
  app.routes['user.posts'] = {
    path: '/u/:username/posts',
    component: PostsUserPage
  };

  // 注册用户积分页面路由
  app.routes['user.points'] = {
    path: '/u/:username/points',
    component: UserPointsPage
  };

  // 注册用户排行榜页面路由（替换 Mentions 路由）
  app.routes['user.mentions'] = {
    path: '/u/:username/mentions',
    component: UserLeaderboardPage
  };

  // 注册用户点赞页面路由
  app.routes['user.likes'] = {
    path: '/u/:username/likes',
    component: UserLikesPage
  };

  // 注册用户群组页面路由
  app.routes['user.groups'] = {
    path: '/u/:username/groups',
    component: UserGroupsPage
  };

  // 扩展 PostsUserPage，去掉 filter[type]=comment，使帖子列表显示所有类型的帖子
  extend(PostsUserPage.prototype, 'loadResults', function (original, offset = 0) {
    return app.store.find('posts', {
      filter: {
        author: this.user.username(),
      },
      page: { offset, limit: this.loadLimit },
      sort: '-createdAt',
    });
  });

  // 扩展 UserPage 的 view 方法，移除 UserCard 并在 nav 内添加个人信息面板
  extend(UserPage.prototype, 'view', function (vnode) {
    const user = this.user;
    if (!user) return vnode;

    // 移除 UserCard Hero UserHero 面板
    const removeUserCard = (nodes) => {
      if (!nodes) return nodes;
      
      if (Array.isArray(nodes)) {
        // 过滤掉 UserCard 组件
        return nodes.filter(node => {
          if (!node) return true;
          
          // 检查 className 是否包含 UserHero 或 Hero
          if (node.attrs && node.attrs.className) {
            const className = typeof node.attrs.className === 'string' 
              ? node.attrs.className 
              : (node.attrs.className && node.attrs.className.toString ? node.attrs.className.toString() : '');
            if (className.includes('UserHero') || className.includes('Hero UserHero')) {
              return false;
            }
          }
          
          // 检查是否是组件，通过检查 tag 的类型
          if (node.tag && typeof node.tag === 'function') {
            // 检查组件名
            const componentName = node.tag.name || node.tag.displayName || '';
            if (componentName.includes('UserCard')) {
              return false;
            }
          }
          
          // 检查是否有 user 属性（UserCard 的特征）
          if (node.attrs && node.attrs.user) {
            // 如果同时有 editable 属性，很可能是 UserCard
            if (node.attrs.hasOwnProperty('editable')) {
              return false;
            }
          }
          
          return true;
        });
      }
      
      return nodes;
    };

    // 递归查找并修改 nav 元素，找到其中的 ul 并添加 UserProfilePanel
    const findAndModifyNav = (node) => {
      if (!node) return false;
      
      // 检查是否是 nav 元素
      if (node.tag === 'nav' && node.attrs && 
          typeof node.attrs.className === 'string' && 
          node.attrs.className.includes('UserPage-nav')) {
        // 找到 nav 元素，查找其中的 ul 元素
        if (Array.isArray(node.children)) {
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            // 查找 ul 元素
            if (child && child.tag === 'ul') {
              // 找到 ul 元素，将 UserProfilePanel 作为第一个子元素
              const existingItems = child.children || [];
              child.children = [
                <li className="item-profilePanel">
                  <UserProfilePanel user={user} />
                </li>,
                ...existingItems
              ];
              return true;
            }
          }
        }
        return false;
      }
      
      // 递归查找子元素
      if (Array.isArray(node.children)) {
        for (let i = 0; i < node.children.length; i++) {
          if (findAndModifyNav(node.children[i])) {
            return true;
          }
        }
      } else if (node.children) {
        return findAndModifyNav(node.children);
      }
      
      return false;
    };

    // 先移除 UserCard
    let modifiedVnode = removeUserCard(vnode);

    // 然后处理 nav 元素
    if (Array.isArray(modifiedVnode)) {
      modifiedVnode.forEach(item => findAndModifyNav(item));
    } else {
      findAndModifyNav(modifiedVnode);
    }

    return modifiedVnode;
  });

  // 扩展 UserPage 的 sidebarItems 方法，替换默认的下拉菜单为直接显示的导航项
  extend(UserPage.prototype, 'sidebarItems', function (items) {
    const user = this.user;
    if (!user) return items;

    // 安全获取 user 的 attributes（兼容不同加载状态）
    const getAttrs = () => {
      if (user.data && user.data.attributes) return user.data.attributes;
      if (typeof user.attributes === 'function') return user.attributes();
      return {};
    };

    // 移除默认的 nav 下拉菜单
    items.remove('nav');

    // 获取当前路由信息
    const currentRoute = app.current.get('routeName');
    const username = user.slug();
    
    // Topics 导航项（优先级 100，显示在第一位）
    // user 路由现在指向 DiscussionsUserPage，所以 user 和 user.discussions 都应该激活 topics
    const isTopicsActive = currentRoute === 'user.discussions' || currentRoute === 'user';
    items.add(
      'topics',
      <a
        className={'UserPage-navItem' + (isTopicsActive ? ' active' : '')}
        href={app.route('user.discussions', { username })}
        onclick={(e) => {
          e.preventDefault();
          m.route.set(app.route('user.discussions', { username }));
        }}
      >
        <i className="icon fa-light fa-bars" />
        <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.topics')}</span>
        <span className="count">{user.discussionCount?.() ?? 0}</span>
      </a>,
      100
    );

    // Posts 导航项（优先级 90，显示在 Topics 之后）
    const isPostsActive = currentRoute === 'user.posts';
    items.add(
      'posts',
      <a
        className={'UserPage-navItem' + (isPostsActive ? ' active' : '')}
        href={app.route('user.posts', { username })}
        onclick={(e) => {
          e.preventDefault();
          m.route.set(app.route('user.posts', { username }));
        }}
      >
        <i className="icon fa-light fa-comment-dots" />
        <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.posts')}</span>
        <span className="count">{Math.max(0, (user.commentCount?.() ?? 0) - (user.discussionCount?.() ?? 0))}</span>
      </a>,
      90
    );

    // Likes 导航项
    const isLikesActive = currentRoute === 'user.likes';
    const likesCount = Number(getAttrs().likesCount) || 0;
    items.add(
      'likes',
      <a
        className={'UserPage-navItem' + (isLikesActive ? ' active' : '')}
        href={app.route('user.likes', { username })}
        onclick={(e) => {
          e.preventDefault();
          m.route.set(app.route('user.likes', { username }));
        }}
      >
        <i className="icon fa-light fa-heart" />
        <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.likes')}</span>
        <span className="count">{likesCount}</span>
      </a>,
      80
    );

    // Groups 导航项
    const isGroupsActive = currentRoute === 'user.groups';
    const userGroupsCount = Number(getAttrs().userGroupsCount) || 0;
    items.add(
      'groups',
      <a
        className={'UserPage-navItem' + (isGroupsActive ? ' active' : '')}
        href={app.route('user.groups', { username })}
        onclick={(e) => {
          e.preventDefault();
          m.route.set(app.route('user.groups', { username }));
        }}
      >
        <i className="icon fa-light fa-users" />
        <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.groups')}</span>
        <span className="count">{userGroupsCount}</span>
      </a>,
      75
    );

    // Mentions 导航项
    // const isMentionsActive = currentRoute === 'user.mentions';
    // items.add(
    //   'mentions',
    //   <a
    //     className={'UserPage-navItem' + (isMentionsActive ? ' active' : '')}
    //     href={app.route('user.mentions', { username })}
    //     onclick={(e) => {
    //       e.preventDefault();
    //       m.route.set(app.route('user.mentions', { username }));
    //     }}
    //   >
    //     <i className="icon fa-light fa-smile" />
    //     <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.mentions')}</span>
    //   </a>,
    //   70
    // );

    // Points 导航项（显示积分值）
    const isPointsActive = currentRoute === 'user.points';
    items.add(
      'points',
      <a
        className={'UserPage-navItem' + (isPointsActive ? ' active' : '')}
        href={app.route('user.points', { username })}
        onclick={(e) => {
          e.preventDefault();
          m.route.set(app.route('user.points', { username }));
        }}
      >
        <i className="icon fa-light fa-star" />
        <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.points')}</span>
      </a>,
      60
    );

    // Orders 导航项（外部链接）
    items.add(
      'orders',
      <a
        className="UserPage-navItem"
        href="https://e.citynewsservice.cn/user/order_list?type=1"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i className="icon fa-light fa-shopping-bag" />
        <span className="label">{app.translator.trans('tf-custom-profile.forum.nav.orders')}</span>
      </a>,
      55
    );

    return items;
  });

  // 扩展 CommentPost，在 PostsUserPage 中为每个帖子的 header 添加面包屑导航
  extend(CommentPost.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    if (!post) return items;

    // 检查当前页面是否是 PostsUserPage
    // 通过检查父组件或路由来判断
    const currentRoute = app.current.get('routeName');
    // user 路由现在指向 discussions，所以只有 user.posts 才是 posts 页面
    const isPostsUserPage = currentRoute === 'user.posts';
    
    if (isPostsUserPage) {
      const discussion = post.discussion();
      if (discussion) {
        // 在 header 开头添加面包屑（优先级高于其他 header 项）
        // 传递 post 参数以便跳转到对应的 post 位置
        items.add(
          'breadcrumb',
          <Breadcrumb discussion={discussion} post={post} />,
          200
        );
      }
    }

    return items;
  });

  // 扩展 PostControls 的 deleteAction，在删除帖子后刷新个人中心帖子列表
  const originalDeleteAction = PostControls.deleteAction;
  PostControls.deleteAction = function(context) {
    // 保存 this 引用（post 对象）
    const post = this;
    const postId = post.id();
    
 
    
    // 调用原始的 deleteAction
    const result = originalDeleteAction.call(this, context);
    
   
    
    // 在删除完成后，检查是否是个人中心帖子页面，如果是则刷新列表
    if (result && typeof result.then === 'function') {
      result.then(() => {
        
        
        const currentRoute = app.current.get('routeName');
        
        
        if (currentRoute === 'user.posts') {
          const currentPage = app.current.get('component');
       
          
        
            
            // 备用方案1: 通过 DOM 操作移除已删除的帖子
            // 查找 PostsUserPage-list 中的所有列表项
            const listItems = document.querySelectorAll('.PostsUserPage-list li');
            
            // 获取讨论信息，用于查找
            const discussion = post.discussion();
            const discussionId = discussion ? discussion.id() : null;
            
            
            let removed = false;
            listItems.forEach((listItem, index) => {
              // 方法1: 查找包含帖子 ID 的链接（可能是 /p/ 或 /d/ 格式）
              const allLinks = listItem.querySelectorAll('a[href]');
              let found = false;
              
              allLinks.forEach((link) => {
                const href = link.getAttribute('href');
                if (href) {
                  // 检查是否包含帖子 ID（可能是 /p/123 或 /d/123/p/456 格式）
                  if (href.includes('/p/' + postId) || href.includes('p=' + postId) || href.includes('post=' + postId)) {
                    found = true;
                   
                  }
                  // 或者通过讨论 ID 查找（如果讨论中只有一个帖子）
                  else if (discussionId && href.includes('/d/' + discussionId)) {
                    // 检查这个列表项是否属于该讨论
                    // 如果讨论中只剩下这个帖子，可以移除
                    found = true;
                    // console.log('[PostControls.deleteAction] 找到讨论链接', {
                    //   index: index,
                    //   href: href,
                    //   discussionId: discussionId
                    // });
                  }
                }
              });
              
              if (found && !removed) {
                listItem.remove();
                removed = true;
               
                const postUser = post.user();
        if (postUser) {
          const userId = postUser.id();
          // 重新加载用户数据以更新 commentCount
          app.store.find('users', userId).then((user) => {
            // 触发重新渲染以更新 UI 中的 commentCount 显示
            m.redraw();
          }).catch((error) => {
            console.error('[PostControls.deleteAction] 重新加载用户数据失败', error);
          });
        } 
              }
            });
            
            if (removed) {
              m.redraw();
            } else {
              
              // 备用方案2: 直接刷新页面（最可靠的方法）
              const username = m.route.param('username');
              if (username) {
                // 延迟一点时间，确保删除操作完成
                setTimeout(() => {
                  // 使用路由刷新，这会重新加载整个页面组件
                  const currentRoute = m.route.get();
                  m.route.set(currentRoute, {}, { replace: true });
                }, 200);
              } else {
              }
            }
          
        } else {
        }
      }).catch((error) => {
      });
    } else {
    }
    
    return result;
  };
});

